"use client";
import { ButtonComponent } from "@/components/index/hero";
import { SanityImage } from "@/components/sanity-image";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { PortableText, PortableTextBlock, PortableTextReactComponents } from "@portabletext/react";
import { ExternalLink } from "lucide-react";
import Link from "next/link";

const TYPE_TO_PATH_SEGMENT_MAP: Record<string, string> = {
  guide: "guides",
  blog: "blogPost",
  page: "",
};

const H1 = ({ children }: { children?: React.ReactNode }) => (
  <h1 className="text-primary-500 font-bold text-2xl lg:text-4xl leading-tight tracking-tight mt-12 mb-6 first:mt-0">
    {children}
  </h1>
);

const H2 = ({ children }: { children?: React.ReactNode }) => (
  <h2 className="text-primary-500 font-semibold text-xl lg:text-3xl leading-snug tracking-tight mt-10 mb-5">
    {children}
  </h2>
);

const H3 = ({ children }: { children?: React.ReactNode }) => (
  <h3 className="text-primary-600 font-semibold text-lg lg:text-2xl leading-snug mt-8 mb-4">
    {children}
  </h3>
);

const H4 = ({ children }: { children?: React.ReactNode }) => (
  <h4 className="text-primary-700 font-medium text-base lg:text-xl leading-snug mt-6 mb-3">
    {children}
  </h4>
);

const H5 = ({ children }: { children?: React.ReactNode }) => (
  <h5 className="text-primary-800 font-medium text-sm lg:text-lg leading-normal mt-5 mb-2 uppercase tracking-wide">
    {children}
  </h5>
);

const H6 = ({ children }: { children?: React.ReactNode }) => (
  <h6 className="text-primary-900 font-medium text-xs lg:text-base leading-normal mt-4 mb-2 tracking-wide">
    {children}
  </h6>
);

const components: Partial<PortableTextReactComponents> = {
  block: {
    h1: H1,
    h2: H2,
    h3: H3,
    h4: H4,
    h5: H5,
    h6: H6,
    normal: ({ children }) => (
      <p className="text-gray-800 text-base lg:text-lg leading-relaxed mb-4">{children}</p>
    ),
  },
  marks: {
    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
    highlight: ({ children }) => (
      <mark className="bg-success-100 text-success-700 font-medium px-1 rounded">{children}</mark>
    ),
    em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
    link: ({ value, children }) => {
      const target = (value?.href || "").startsWith("http") ? "_blank" : undefined;
      const rel = target === "_blank" ? "noopener noreferrer" : undefined;
      return (
        <Link
          href={value?.href}
          target={target}
          rel={rel}
          className="text-primary-500 hover:text-primary-600 underline decoration-primary-300 hover:decoration-primary-500 underline-offset-2 transition-colors"
        >
          {children}
        </Link>
      );
    },
    internalLink: ({ value, children }) => {
      const segment = TYPE_TO_PATH_SEGMENT_MAP[value.reference._type]
        ? `/${TYPE_TO_PATH_SEGMENT_MAP[value.reference._type]}`
        : "";
      if (!value?.reference) {
        return <span>{children}</span>;
      }
      const href = `${segment}/${value.reference.slug}`;
      return (
        <Link
          href={href}
          className="text-primary-500 hover:text-primary-600 underline decoration-primary-300 hover:decoration-primary-500 underline-offset-2 transition-colors inline-flex items-center gap-1"
        >
          {children}
          <ExternalLink size={14} className="inline-block" />
        </Link>
      );
    },
  },
  list: {
    bullet: ({ children }) => (
      <ul className="space-y-2 list-disc list-outside ml-6 mb-6 text-gray-800">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="space-y-2 list-decimal list-outside ml-6 mb-6 text-gray-800">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="text-base lg:text-lg leading-relaxed pl-2">{children}</li>
    ),
    number: ({ children }) => (
      <li className="text-base lg:text-lg leading-relaxed pl-2">{children}</li>
    ),
  },
  types: {
    button: ButtonComponent,
    image: ({ value }) => {
      if (!value?.asset) return null;

      const dimensions = value.asset.metadata?.dimensions;
      const aspectRatio = dimensions?.aspectRatio || 16 / 9;
      const imageWidth = dimensions?.width;

      // Constrain max width to actual image width to prevent blur
      const maxWidth = imageWidth ? `${imageWidth}px` : "100%";

      return (
        <figure className="my-8 w-full flex flex-col items-start">
          <div style={{ maxWidth }} className="w-full p-3 bg-white rounded-xl shadow">
            <AspectRatio ratio={aspectRatio} className="bg-gray-100 rounded-lg overflow-hidden">
              <SanityImage
                src={value}
                alt={value.caption || value.alt || "Guide image"}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 768px, 896px"
              />
            </AspectRatio>
          </div>
          {value.caption && (
            <figcaption
              style={{ maxWidth }}
              className="w-full mt-3 text-sm lg:text-base text-gray-600 italic text-center"
            >
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
};

export function GuideContent({ body }: { body: PortableTextBlock[] }) {
  return (
    <section className="gap-y-8 p-2 bg-white">
      <PortableText value={body} components={components} />
    </section>
  );
}
